import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { Formik, Field, Form } from 'formik';
import { validatePhoneNumber } from '../../../../../actions/safecheckActions';
import { fetchCurrentTravelerUser } from '../../../../../actions/profileActions';
import ViaModal from '../../../../components/library/viaModal';
import Headline from '../../../../components/library/headline';
import Paragraph from '../../../../components/library/paragraph';
import InputCellPhone from '../../../../components/library/inputCellPhone';
import Button from '../../../../components/library/button';
import SuccessSafeCheckValidated from '../../../../plans/components/modals/successSafeCheckValidated';
import EditSafeCheck from './editSafeCheckPhone';
import sSafecheckPhone from './safecheckPhone.scss';
import withStyles from 'isomorphic-style-loader/lib/withStyles';

export const enhance = compose(
  withStyles(sSafecheckPhone),
  withRouter,
);

function SafecheckPhone(props) {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);
  const [error, setError] = useState('');
  const orgInfo = useSelector(state => state.profile.clientAccountName);
  const customMessage = useSelector(state => state.profile.safeCheckCustomMessage);
  const safeCheckValid = useSelector(state => state.profile.safecheck);
  const safecheckValidated = useSelector(state => state.safecheck.validatePhoneNumber);
  const [saving, setSaving] = useState(false);
  const [showSuccessSafeCheck, setShowSuccessSafeCheck] = useState(false);
  const userId = useSelector(state => state.profile.currentUser.id);

  useEffect(
    () => {
      if (safecheckValidated) {
        if (safecheckValidated.loading) {
          setSaving(true);
        }

        if (safecheckValidated.validatedPhoneNumber.phone_number) {
          setShowSuccessSafeCheck(true);
          setError('');
          dispatch(fetchCurrentTravelerUser(userId));
        } else if (safecheckValidated.error) {
          if (safecheckValidated.error === 'Invalid phone number') {
            setError(`Number cannot be verified. Please try again.`);
            setShowSuccessSafeCheck(false);
          } else {
            setError(safecheckValidated.error);
            setShowSuccessSafeCheck(false);
          }
        }
      }

      if (saving && !!safecheckValidated.error) {
        setSaving(false);
      }
    },
    [safecheckValidated],
  );

  return (
    <>
      {showSuccessSafeCheck ? (
        <ViaModal
          showing={showSuccessSafeCheck}
          onClose={() => {
            setShowSuccessSafeCheck(false);
          }}
          headerText="Successful Validation"
        >
          <SuccessSafeCheckValidated
            show={showSuccessSafeCheck}
            onClose={() => {
              setShowSuccessSafeCheck(false);
            }}
          />
        </ViaModal>
      ) : safeCheckValid ? (
        <div className={sSafecheckPhone['safecheck-phone']}>
          <EditSafeCheck
            setShowSuccessSafeCheck={() => setShowSuccessSafeCheck(false)}
            safeCheckPhoneNumber={safeCheckValid}
            userId={userId}
            showing={showSuccessSafeCheck}
          />
        </div>
      ) : (
        <div className={sSafecheckPhone['safecheck-phone']}>
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            initialValues={defaultValue}
            validate={values => {
              const errors = {};
              if (!values.safecheck_phone) {
                errors.safecheck_phone = 'Valid Phone Number Required';
              }
              return errors;
            }}
            onSubmit={values => {
              const submitValues = {
                phone_number: `+${values.safecheck_phone}`,
              };
              dispatch(validatePhoneNumber(`${submitValues.phone_number}`));
            }}
          >
            {({ values, errors, setFieldValue, handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <div className={sSafecheckPhone['safecheck-phone-header']}>
                  <Headline tag="h1" as="h1">
                    One-Step free Enrollment in SafeCheck
                  </Headline>
                </div>
                <div className={sSafecheckPhone['safecheck-phone-header']}>
                  <Headline tag="h2" as="h2">
                    SafeCheck is a free health, safety, emergency-response, and wellness communication check-in service
                    available to you while you travel.
                  </Headline>
                </div>
                <div>
                  <Paragraph>
                    To enroll for free:
                    <br />
                    1. Enter your travel cell or mobile phone number below.
                    <br />
                    2. Receive a confirmation text.
                    <br />
                    3. You're enrolled!
                  </Paragraph>
                </div>
                <div className={sSafecheckPhone['safecheck-phone-header']}>
                  <Headline tag="h2" as="h2">
                    Terms & Conditions
                  </Headline>
                </div>
                <div className={sSafecheckPhone['safecheck-phone-paragraph']}>
                  <Paragraph bold>The Basics</Paragraph>
                </div>
                <div className={sSafecheckPhone['safecheck-phone-paragraph']}>
                  <Paragraph>
                    You can opt-out at any time. Normal text rates may apply.
                    <br />
                    <br />
                    Enrolling in SafeCheck means you may occasionally receive a SMS text message from {orgInfo} while
                    traveling and will have the option to confirm your safety, share a custom message with {orgInfo},
                    and may have the option to share a snapshot of your location. {orgInfo} cannot and will not be able
                    to track your ongoing location with SafeCheck.
                    <br />
                    <br />
                    SafeCheck is not a marketing service and you will not receive advertisements or offers. Your
                    information cannot and will not be sold or shared with third party organizations without your
                    permission.
                  </Paragraph>
                </div>
                <div className={sSafecheckPhone['safecheck-phone-paragraph']}>
                  <Paragraph bold>Additional Terms</Paragraph>
                </div>
                <div className={sSafecheckPhone['safecheck-phone-paragraph']}>
                  <Paragraph>{customMessage}</Paragraph>
                </div>
                <div className={sSafecheckPhone['safecheck-phone-input']}>
                  <Field name="safecheck_phone">
                    {({ form }) => (
                      <InputCellPhone
                        id="safecheck_phone"
                        name="safecheck_phone"
                        helpText="Enter in the country code & phone number you'll have with you while you travel."
                        isRequired
                        value={defaultValue}
                        errorMessage={errors.safecheck_phone ? errors.safecheck_phone : error}
                        labelText={``}
                        onChange={e => {
                          form.setFieldValue('safecheck_phone', e);
                        }}
                      />
                    )}
                  </Field>
                </div>
                <div className={sSafecheckPhone.footer}>
                  <div className={sSafecheckPhone['footer-button-wrapper']}>
                    <div className={sSafecheckPhone['button-content']}>
                      <Button display="primary" kind="solid" size="medium" type="submit" loading={saving}>
                        Enroll
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </>
  );
}

export default enhance(SafecheckPhone);
